import { useState } from 'react';
import PropTypes from 'prop-types';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { MONTH_NAMES, moduleBadge, fmtIntensity, intensityTickFmt } from '@/utils/gconUtils';

const TREND_META = {
  improving: { label: 'Improving', color: 'text-green-600', barFill: '#00A99D', lineColor: '#00A99D' },
  degrading: { label: 'Degrading', color: 'text-red-600', barFill: '#FF8F12', lineColor: '#DC2626' },
  stable: { label: 'Stable', color: 'text-gray-500', barFill: '#94a3b8', lineColor: '#94a3b8' },
};

const IntensityChart = ({ rows, trend, uom }) => {
  const valid = rows.filter(r => r.intensity !== null);
  if (!valid.length) return <p className="text-xs text-gray-400 py-6 text-center">No intensity data available.</p>;

  const meta = TREND_META[trend] ?? TREND_META.stable;
  const maxY = Math.max(...valid.map(r => r.intensity), 0.001);
  const n = valid.length;

  const VW = 560,
    VH = 200,
    ML = 58,
    MB = 28,
    chartW = VW - ML,
    chartH = VH - MB;
  const barW = Math.max(chartW / n - 4, 4);

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(f => ({
    val: maxY * f,
    y: VH - MB - f * chartH,
  }));

  const tick = intensityTickFmt(maxY * 1000);

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full" style={{ height: 200 }}>
      {yTicks.map(({ val, y }) => (
        <g key={y}>
          <line x1={ML} x2={VW} y1={y} y2={y} stroke="#f0f0f0" strokeWidth="1" />
          <text x={ML - 4} y={y + 4} fontSize="9" fill="#9ca3af" textAnchor="end">
            {tick(val * 1000)}
          </text>
        </g>
      ))}

      {valid.map((r, i) => {
        const barH = Math.max((r.intensity / maxY) * chartH, 1);
        const bx = ML + (i / n) * chartW + (chartW / n - barW) / 2;
        const by = VH - MB - barH;
        const fill = r.is_anomaly ? '#f59e0b' : meta.barFill;
        return (
          <g key={i}>
            <rect x={bx} y={by} width={barW} height={barH} fill={fill} rx="2" opacity={r.is_anomaly ? 1 : 0.85} />
            {r.is_anomaly && (
              <>
                <circle cx={bx + barW / 2} cy={by - 5} r="3" fill="#f59e0b" />
                <line
                  x1={bx + barW / 2}
                  y1={by - 10}
                  x2={bx + barW / 2}
                  y2={by - 14}
                  stroke="#f59e0b"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </>
            )}
            <text
              x={bx + barW / 2}
              y={VH - MB + 12}
              fontSize="9"
              fill={r.is_anomaly ? '#d97706' : '#6b7280'}
              fontWeight={r.is_anomaly ? 'bold' : 'normal'}
              textAnchor="middle"
            >
              {MONTH_NAMES[(r.month - 1) % 12]}
            </text>
          </g>
        );
      })}

      <line x1={ML} x2={ML} y1={0} y2={VH - MB} stroke="#e5e7eb" strokeWidth="1" />
      <line x1={ML} x2={VW} y1={VH - MB} y2={VH - MB} stroke="#e5e7eb" strokeWidth="1" />

      <text transform={`rotate(-90) translate(-${VH / 2}, 12)`} fontSize="9" fill="#9ca3af" textAnchor="middle">
        {`kg CO₂e / ${uom || 'unit'}`}
      </text>
    </svg>
  );
};

/**
 * Wilkinson-style dot plot: months are placed along the intensity axis and
 * stack upward where they'd collide, so clusters show the operating band and
 * strays show outlier months.
 */
const IntensityDotStrip = ({ rows, trend, uom }) => {
  const valid = rows.filter(r => r.intensity !== null);
  if (!valid.length) return <p className="text-xs text-gray-400 py-6 text-center">No intensity data available.</p>;

  const meta = TREND_META[trend] ?? TREND_META.stable;
  const values = valid.map(r => r.intensity);
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const lo = Math.min(...values);
  const hi = Math.max(...values);

  // Anchoring at zero collapses a tight cluster into one column, so window the
  // axis on the spread instead and keep the mean dead centre. Clamp at zero
  // rather than render a negative intensity; a far outlier wins over centring.
  // Guard against float noise reading as a real (but infinitesimal) spread
  const rawSpread = Math.max(mean - lo, hi - mean);
  const spread = rawSpread > Math.abs(mean) * 1e-9 ? rawSpread : 0;
  const halfSpan = (spread > 0 ? spread : Math.max(mean * 0.05, 1e-9)) * 1.25;
  const axisMin = Math.max(mean - halfSpan, 0);
  const axisMax = axisMin === 0 ? Math.max(mean * 2, hi * 1.05, 1e-9) : mean + halfSpan;
  const span = axisMax - axisMin;

  const VW = 560,
    VH = 200,
    ML = 18,
    MR = 18,
    MB = 34,
    MT = 20;
  const chartW = VW - ML - MR;
  const baseline = VH - MB;
  const R = 9;

  // Precision has to follow the tick step, not the axis max — a zoomed window
  // like 1.94→2.06 would otherwise print every tick as "2".
  const stepKg = (span * 1000) / 4;
  const dp = Math.max(0, Math.min(4, Math.ceil(-Math.log10(stepKg || 1)) + 1));
  const tick = kg => kg.toFixed(dp);
  const xOf = v => ML + ((v - axisMin) / span) * chartW;

  // Stack any dots that would overlap horizontally
  const placed = [];
  const dots = [...valid]
    .sort((a, b) => a.intensity - b.intensity)
    .map(r => {
      const x = xOf(r.intensity);
      let level = 0;
      while (placed.some(p => p.level === level && Math.abs(p.x - x) < R * 2)) level++;
      placed.push({ x, level });
      return { r, x, level };
    });

  // A tall stack (many months at one value) would overflow the top, so squeeze
  // the row spacing — and the dots with it — to fit the plot area.
  const maxLevel = Math.max(...dots.map(d => d.level), 0);
  const STEP = Math.min(R * 2 + 2, (baseline - MT) / (maxLevel + 1));
  const dotR = Math.min(R, STEP / 2 - 1);
  const showMonthLabels = dotR >= 7;

  const xTicks = [0, 0.25, 0.5, 0.75, 1].map(f => {
    const val = axisMin + span * f;
    return { val, x: xOf(val) };
  });

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full" style={{ height: 200 }}>
      {xTicks.map(({ val, x }) => (
        <g key={x}>
          <line x1={x} x2={x} y1={8} y2={baseline} stroke="#f0f0f0" strokeWidth="1" />
          <text x={x} y={baseline + 14} fontSize="9" fill="#9ca3af" textAnchor="middle">
            {tick(val * 1000)}
          </text>
        </g>
      ))}

      {/* Mean */}
      <line x1={xOf(mean)} x2={xOf(mean)} y1={8} y2={baseline} stroke="#94a3b8" strokeWidth="1" strokeDasharray="3 3" />
      <text x={xOf(mean)} y={14} fontSize="8" fill="#94a3b8" textAnchor="middle">
        mean
      </text>

      <line x1={ML} x2={VW - MR} y1={baseline} y2={baseline} stroke="#e5e7eb" strokeWidth="1" />

      {dots.map(({ r, x, level }, i) => {
        const cy = baseline - dotR - level * STEP;
        const fill = r.is_anomaly ? '#f59e0b' : meta.barFill;
        return (
          <g key={i}>
            <circle
              cx={x}
              cy={cy}
              r={dotR}
              fill={fill}
              opacity={r.is_anomaly ? 1 : 0.85}
              stroke={r.is_anomaly ? '#d97706' : '#ffffff'}
              strokeWidth={r.is_anomaly ? 1.5 : 1}
            >
              <title>
                {`${MONTH_NAMES[(r.month - 1) % 12]} ${r.year}: ${fmtIntensity(r.intensity)} kg CO₂e/${uom || 'unit'}${r.is_anomaly ? ' · anomaly' : ''}`}
              </title>
            </circle>
            {showMonthLabels && (
              <text
                x={x}
                y={cy + 2.5}
                fontSize="7"
                fontWeight="600"
                fill="#ffffff"
                textAnchor="middle"
                pointerEvents="none"
              >
                {MONTH_NAMES[(r.month - 1) % 12]}
              </text>
            )}
          </g>
        );
      })}

      <text x={ML + chartW / 2} y={VH - 4} fontSize="9" fill="#9ca3af" textAnchor="middle">
        {`kg CO₂e / ${uom || 'unit'}${axisMin > 0 ? ' · axis zoomed to range' : ''}`}
      </text>
    </svg>
  );
};

const MonthAnomalyHeatmap = ({ sources }) => {
  const counts = Array(12).fill(0);
  for (const s of sources) {
    for (const r of s.monthly_levelized ?? []) {
      if (r.is_anomaly) counts[r.month - 1]++;
    }
  }
  const maxCount = Math.max(...counts, 1);
  const total = counts.reduce((a, b) => a + b, 0);

  if (total === 0) return null;

  return (
    <div className="px-5 pt-4 pb-3 border-b border-gray-100 flex-shrink-0">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        Anomalies by Month — all sources ({total} total)
      </p>
      <div className="flex gap-1">
        {MONTH_NAMES.map((mon, i) => {
          const count = counts[i];
          const intensity = count / maxCount;
          return (
            <div
              key={mon}
              className="flex-1 flex flex-col items-center gap-0.5"
              title={`${mon}: ${count} anomal${count === 1 ? 'y' : 'ies'}`}
            >
              <div
                className="w-full rounded"
                style={{
                  height: 20,
                  background: count === 0 ? '#f3f4f6' : `rgba(245, 158, 11, ${0.2 + intensity * 0.8})`,
                }}
              />
              <span className={`text-xs font-semibold ${count > 0 ? 'text-amber-600' : 'text-gray-300'}`}>
                {count > 0 ? count : '·'}
              </span>
              <span className="text-xs text-gray-400">{mon}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const LevelizationView = ({ sources }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [chartMode, setChartMode] = useState('bars'); // bars | dots

  if (!sources.length) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
        <ChartBarIcon className="w-10 h-10" />
        <p className="text-sm">No sources could be levelized.</p>
        <p className="text-xs text-center max-w-md">
          Levelization uses each source&apos;s <em>Energy Used (LHV)</em> from the GCON Output, or otherwise its flow
          from the GCON Input&apos;s <em>Import_Volume</em> sheet. Neither was found for these sources.
        </p>
      </div>
    );
  }

  // Derived each render so a month-filter change never leaves stale rows on screen
  const sel = sources.find(s => s.source_id === selectedId) ?? sources[0];
  const rows = sel?.monthly_levelized ?? [];
  const trend = sel?.trend ?? 'stable';

  return (
    <div className="flex h-full min-h-0 gap-0">
      {/* Source list */}
      <div className="w-64 flex-shrink-0 border-r border-gray-100 overflow-y-auto flex flex-col">
        {sources.map(s => {
          const isSelected = s.source_id === sel?.source_id;
          return (
            <button
              key={s.source_id}
              type="button"
              onClick={() => setSelectedId(s.source_id)}
              className={`w-full text-left px-3 py-2.5 border-b border-gray-50 hover:bg-gray-50 transition-colors
                ${isSelected ? 'bg-teal-50 border-l-2 border-l-teal-500' : ''}`}
            >
              <p className={`text-xs font-medium truncate ${isSelected ? 'text-teal-700' : 'text-gray-700'}`}>
                {s.source_name}
              </p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${moduleBadge(s.module)}`}>
                  {s.module || '—'}
                </span>
                {s.anomaly_count > 0 && (
                  <span className="text-xs px-1.5 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-700">
                    ⚠ {s.anomaly_count}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Detail panel */}
      <div className="flex-1 flex flex-col min-h-0">
        <MonthAnomalyHeatmap sources={sources} />
        <div className="flex-1 overflow-y-auto p-5">
          {!sel ? (
            <p className="text-sm text-gray-400">Select a source to view its intensity trend.</p>
          ) : (
            <>
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-800">{sel.source_name}</h3>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${moduleBadge(sel.module)}`}>
                      {sel.module || '—'}
                    </span>
                    {sel.location && (
                      <span className="text-xs text-gray-400">
                        {sel.location}
                        {sel.sub_location ? ` / ${sel.sub_location}` : ''}
                      </span>
                    )}
                    {sel.uom && (
                      <span
                        className="text-xs px-2 py-0.5 rounded-full font-medium bg-teal-100 text-teal-700"
                        title={
                          sel.basis === 'energy'
                            ? 'Levelized on energy used (LHV) from the GCON Output'
                            : 'Levelized on flow from the GCON Input (Import_Volume)'
                        }
                      >
                        {sel.basis === 'energy' ? 'Per energy used' : 'Per flow'} · {sel.uom}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Intensity variability stats */}
              {sel.variability != null && (
                <div className="flex gap-3 mb-4">
                  {[
                    { label: 'σ (intensity)', value: `${fmtIntensity(sel.variability)} kg` },
                    { label: 'CV', value: `${(sel.cv * 100).toFixed(1)}%` },
                    { label: 'Anomalies', value: sel.anomaly_count, highlight: sel.anomaly_count > 0 },
                  ].map(s => (
                    <div key={s.label} className="flex-1 bg-gray-50 rounded-lg px-3 py-2">
                      <p className="text-xs text-gray-400 mb-0.5">{s.label}</p>
                      <p className={`text-sm font-semibold ${s.highlight ? 'text-amber-600' : 'text-gray-700'}`}>
                        {s.value}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Chart */}
              <div className="bg-white border border-gray-100 rounded-lg p-3 mb-4">
                <div className="flex items-center justify-between mb-2 gap-3">
                  <p className="text-xs font-medium text-gray-500">
                    {chartMode === 'bars'
                      ? `Monthly Emission Intensity (kg CO₂e / ${sel.uom || 'unit'})`
                      : `Intensity Distribution by Month (kg CO₂e / ${sel.uom || 'unit'})`}
                  </p>
                  <div className="join flex-shrink-0">
                    {[
                      { key: 'bars', label: 'Monthly' },
                      { key: 'dots', label: 'Distribution' },
                    ].map(opt => (
                      <button
                        key={opt.key}
                        type="button"
                        className={`join-item btn btn-xs ${chartMode === opt.key ? 'btn-primary' : 'btn-ghost'}`}
                        onClick={() => setChartMode(opt.key)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                {chartMode === 'bars' ? (
                  <IntensityChart rows={rows} trend={trend} uom={sel.uom} />
                ) : (
                  <IntensityDotStrip rows={rows} trend={trend} uom={sel.uom} />
                )}
              </div>

              {/* Data table */}
              <div className="bg-white border border-gray-100 rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-3 py-2 text-gray-500 font-medium">Month</th>
                      <th className="text-right px-3 py-2 text-gray-500 font-medium">{`Flow (${sel.uom || 'unit'})`}</th>
                      <th className="text-right px-3 py-2 text-gray-500 font-medium">CO₂e (t)</th>
                      <th className="text-right px-3 py-2 text-gray-500 font-medium">{`Intensity (kg CO₂e/${sel.uom || 'unit'})`}</th>
                      <th className="text-right px-3 py-2 text-gray-500 font-medium">Δ vs prev</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const prev = rows[i - 1];
                      const delta =
                        prev?.intensity != null && r.intensity != null ? r.intensity - prev.intensity : null;
                      const fmt = v => (v == null ? '—' : fmtIntensity(v));
                      const fmtDelta = v =>
                        v == null ? '—' : `${v > 0 ? '+' : v < 0 ? '−' : ''}${fmtIntensity(Math.abs(v))}`;
                      return (
                        <tr
                          key={i}
                          className={`border-b border-gray-50 ${r.is_anomaly ? 'bg-amber-50' : 'hover:bg-gray-50'}`}
                        >
                          <td className={`px-3 py-2 font-medium ${r.is_anomaly ? 'text-amber-800' : 'text-gray-700'}`}>
                            {MONTH_NAMES[(r.month - 1) % 12]} {r.year}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-600">
                            {(r.flow ?? 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-600">{r.co2e.toFixed(2)}</td>
                          <td
                            className={`px-3 py-2 text-right font-medium ${r.is_anomaly ? 'text-amber-700' : 'text-gray-800'}`}
                          >
                            {fmt(r.intensity)}
                          </td>
                          <td
                            className={`px-3 py-2 text-right font-medium
                          ${delta == null ? 'text-gray-300' : delta > 0 ? 'text-red-500' : delta < 0 ? 'text-green-600' : 'text-gray-400'}`}
                          >
                            {fmtDelta(delta)}
                          </td>
                          <td className="px-2 py-2 text-center w-6">
                            {r.is_anomaly && (
                              <span
                                title="Anomaly — intensity is a statistical outlier (IQR method)"
                                className="text-amber-500 text-xs"
                              >
                                ⚠
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// By Unit view
// ---------------------------------------------------------------------------

IntensityChart.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  trend: PropTypes.string,
  uom: PropTypes.string,
};

IntensityDotStrip.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  trend: PropTypes.string,
  uom: PropTypes.string,
};

MonthAnomalyHeatmap.propTypes = { sources: PropTypes.arrayOf(PropTypes.object).isRequired };

LevelizationView.propTypes = { sources: PropTypes.arrayOf(PropTypes.object).isRequired };

export default LevelizationView;
